import {
  DOOM_PLATFORMS, PLATFORM_ALIASES, VIDEO_CACHE_KEY,
  YOUTUBE_API_KEY, YOUTUBE_CHANNEL_ID, YT_PAGE_SIZE,
} from './config';
import { cleanTitle, isoDurationToSeconds, slugify } from './slug';

export interface Thumbnail {
  url: string;
  width?: number;
  height?: number;
}

export interface VideoMeta {
  id: string;
  title: string;
  rawTitle: string;
  description: string;
  publishedAt: string;
  thumbnails: Record<string, Thumbnail>;
  category: string;
  /** Present only for character/sport playthroughs. */
  charLabel?: string;
}

// --- Categorisation ---------------------------------------------------
// Inspect a (cleaned) title and produce a `{ id, label }` decision. The
// id is one of:
//   excluded            — short gameplay clip, drop it
//   superplay           — high-skill run
//   compilation         — best-of montage
//   playthrough         — generic full playthrough
//   playthrough-<plat>  — platform-specific full playthrough
//   doom-playthrough-…  — Doom variant per platform
//   char-playthrough-…  — character playthrough; label = "<char> (<plat>)"
//   sport-playthrough-… — sport vs. matchup
//   other               — anything that doesn't fit (drop it)
function detectCategory(title: string): { id: string; label: string | null } {
  const t = title.toLowerCase();

  if (t.includes('gameplay')) return { id: 'excluded', label: null };
  if (t.includes('superplay')) return { id: 'superplay', label: null };
  if (t.includes('compilation')) return { id: 'compilation', label: null };

  if (t.includes('playthrough') || t.includes('play through')) {
    // "<Game> (<Platform>) Playthrough" pattern → platform-specific bucket.
    for (const { category, aliases } of PLATFORM_ALIASES) {
      for (const alias of aliases) {
        const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        if (new RegExp(`\\(${escaped}\\)\\W+playthrough`, 'i').test(title)) {
          return { id: category, label: null };
        }
      }
    }
    // "<Character Name> (<Platform>)" without an explicit playthrough word →
    // character playthrough bucket. label is the "Character (Platform)" pair.
    for (const { aliases } of PLATFORM_ALIASES) {
      for (const alias of aliases) {
        const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const m = title.match(new RegExp(`^(.+?)\\s*\\(${escaped}\\)`, 'i'));
        if (m) {
          const name = m[1].trim();
          const canonicalPlatform =
            PLATFORM_ALIASES.find((p) =>
              p.aliases.some((a) => a.toLowerCase() === alias.toLowerCase()),
            )?.aliases[0] ?? alias;
          return {
            id: `char-playthrough-${slugify(name)}-${slugify(canonicalPlatform)}`,
            label: `${name} (${canonicalPlatform})`,
          };
        }
      }
    }
    return { id: 'playthrough', label: null };
  }

  // Doom variants get a per-platform bucket.
  if (/^doom\s*\(/i.test(title)) {
    for (const { id, aliases } of DOOM_PLATFORMS) {
      for (const alias of aliases) {
        const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        if (new RegExp(`\\(${escaped}\\)`, 'i').test(title)) {
          return { id, label: null };
        }
      }
    }
  }

  // "Team A vs. Team B (<Platform>)" → sport bucket with the team line as label.
  if (/\bvs\./i.test(title)) {
    for (const { aliases } of PLATFORM_ALIASES) {
      for (const alias of aliases) {
        const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const m = title.match(new RegExp(`^(.+?)\\s*\\(${escaped}\\)`, 'i'));
        if (m) {
          const matchup = m[1].trim();
          const canonicalPlatform =
            PLATFORM_ALIASES.find((p) =>
              p.aliases.some((a) => a.toLowerCase() === alias.toLowerCase()),
            )?.aliases[0] ?? alias;
          return {
            id: `sport-playthrough-${slugify(matchup)}-${slugify(canonicalPlatform)}`,
            label: `${matchup} (${canonicalPlatform})`,
          };
        }
      }
    }
    return { id: 'sport-playthrough', label: null };
  }

  return { id: 'other', label: null };
}

// --- Cache ---------------------------------------------------------------
//
// We persist the full video list in localStorage with NO automatic expiry.
// The intent is to never burn YouTube API quota silently — a refresh only
// happens when the user explicitly clicks the Refresh button (which calls
// refreshVideosFromApi() below).
//
// VIDEO_CACHE_TTL_MS exists as a kept-for-compatibility constant but is no
// longer used to invalidate.

function readCache(): { videos: VideoMeta[]; timestamp: number } | null {
  try {
    const raw = localStorage.getItem(VIDEO_CACHE_KEY);
    if (!raw) return null;
    const { timestamp, videos } = JSON.parse(raw);
    if (!Array.isArray(videos) || videos.length === 0) return null;
    return { videos, timestamp: Number(timestamp) || 0 };
  } catch {
    return null;
  }
}

// When was the cache last populated? Used by the UI to display "Updated 5 minutes ago".
export function getCacheTimestamp(): number | null {
  const entry = readCache();
  return entry?.timestamp ?? null;
}

// Clear the cache. Next call to fetchAllVideos() will fall back to the
// bundled /data/videos.json snapshot.
export function clearCache(): void {
  try { localStorage.removeItem(VIDEO_CACHE_KEY); } catch { /* noop */ }
}

function writeCache(videos: VideoMeta[]) {
  try {
    localStorage.setItem(
      VIDEO_CACHE_KEY,
      JSON.stringify({ timestamp: Date.now(), videos }),
    );
  } catch {
    // Quota / private mode — fine, just skip.
  }
}

// --- API plumbing --------------------------------------------------------

async function ytFetch(endpoint: string, params: Record<string, unknown>) {
  const url = new URL(`https://www.googleapis.com/youtube/v3/${endpoint}`);
  url.searchParams.set('key', YOUTUBE_API_KEY);
  for (const [k, v] of Object.entries(params)) {
    if (v != null) url.searchParams.set(k, String(v));
  }
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`YouTube API error ${res.status}: ${res.statusText}`);
  const data = await res.json();
  if (data.error) throw new Error(`YouTube API error: ${JSON.stringify(data.error)}`);
  return data;
}

// Channel → uploads playlist id. Cached at module scope so we don't re-call
// it on every fetchAllVideos() round.
let uploadsPlaylistId: string | null = null;
async function getUploadsPlaylistId(): Promise<string> {
  if (uploadsPlaylistId) return uploadsPlaylistId;
  const data = await ytFetch('channels', {
    part: 'contentDetails,snippet',
    id: YOUTUBE_CHANNEL_ID,
  });
  if (!data.items?.length) throw new Error(`Channel not found for id ${YOUTUBE_CHANNEL_ID}`);
  const id = data.items[0].contentDetails.relatedPlaylists.uploads;
  if (!id) throw new Error('Uploads playlist not found');
  uploadsPlaylistId = id;
  return id;
}

// Look up one specific video by slug — used by the /video/:slug route when
// the user lands on it cold (no in-memory cache).
export async function findVideoIdBySlug(slug: string): Promise<string> {
  if (!slug) throw new Error('Video slug is required');
  const data = await ytFetch('search', {
    part: 'snippet',
    q: slug.replace(/-/g, ' '),
    type: 'video',
    maxResults: 5,
    channelId: YOUTUBE_CHANNEL_ID,
  });
  if (!data.items?.length) throw new Error(`No video found for slug ${slug}`);
  const exact = data.items.find((item: { snippet: { title: string } }) =>
    slugify(cleanTitle(item.snippet.title)) === slug,
  );
  return (exact ?? data.items[0]).id.videoId;
}

// Last-resort: read /data/videos.json from public/. Used when the API
// can't be reached (no network, dev mode, quota exhausted), or whenever
// VITE_USE_LOCAL=1 is set so we bypass the API entirely.
async function readLocalSnapshot(): Promise<VideoMeta[] | null> {
  try {
    const res = await fetch('/data/videos.json', { cache: 'no-cache' });
    if (!res.ok) return null;
    const data = await res.json();
    if (!Array.isArray(data?.videos) || data.videos.length === 0) return null;
    return data.videos as VideoMeta[];
  } catch {
    return null;
  }
}

// Resolve the video list for a normal page load. NEVER hits the YouTube API.
// Order of preference:
//   1. localStorage cache (persisted forever after the last manual refresh)
//   2. Bundled snapshot at /data/videos.json (the build-time baseline)
//
// To pick up new videos, the user must explicitly call refreshVideosFromApi()
// (wired to a "Refresh" button in the UI). This avoids automatically burning
// YouTube Data API quota every time someone visits the site.
export async function fetchAllVideos(): Promise<VideoMeta[]> {
  const cached = readCache();
  if (cached) return cached.videos;

  const local = await readLocalSnapshot();
  if (local) return local;

  // No cache, no snapshot — nothing we can do without an API hit, which the
  // user has to authorise. Return empty so the UI shows "no videos".
  return [];
}

// Internal: paginate the channel's uploads playlist and return categorised
// metadata. Shared by both the runtime refresh button and the build-time
// `npm run refresh-videos` script.
async function fetchFromApi(): Promise<VideoMeta[]> {
  const playlistId = await getUploadsPlaylistId();
  const allIds: string[] = [];
  let pageToken: string | null = null;

  do {
    const params: Record<string, unknown> = {
      part: 'contentDetails',
      playlistId,
      maxResults: YT_PAGE_SIZE,
    };
    if (pageToken) params.pageToken = pageToken;
    const data = await ytFetch('playlistItems', params);
    for (const item of data.items ?? []) {
      allIds.push(item.contentDetails.videoId);
    }
    pageToken = data.nextPageToken ?? null;
  } while (pageToken);

  // Fetch full metadata in batches of 50 (the API max).
  const out: VideoMeta[] = [];
  for (let i = 0; i < allIds.length; i += 50) {
    const batch = await ytFetch('videos', {
      part: 'snippet,contentDetails',
      id: allIds.slice(i, i + 50).join(','),
    });
    for (const item of batch.items ?? []) {
      if (isoDurationToSeconds(item.contentDetails?.duration) <= 60) continue;
      const cleaned = cleanTitle(item.snippet.title);
      const { id: category, label } = detectCategory(cleaned);
      if (category === 'excluded' || category === 'other') continue;
      out.push({
        id: item.id,
        title: cleaned,
        rawTitle: item.snippet.title,
        description: item.snippet.description,
        publishedAt: item.snippet.publishedAt,
        thumbnails: item.snippet.thumbnails,
        category,
        ...(label !== null ? { charLabel: label } : {}),
      });
    }
  }

  return out;
}

// Explicit manual refresh: hit the YouTube API, replace the localStorage
// cache, return the new list. Wired to the "Refresh videos" button.
// Throws on any failure (quota, network) — callers should show that to the
// user so they understand why the refresh didn't happen.
export async function refreshVideosFromApi(): Promise<VideoMeta[]> {
  const fresh = await fetchFromApi();
  writeCache(fresh);
  return fresh;
}
